/**
 * Central API client. Sends the session cookie and the CSRF header, unwraps the JSON
 * envelope and throws `ApiError` with the server's (Arabic) message.
 */
export class ApiError extends Error {
  constructor(
    public status: number,
    public code: string,
    message: string,
    public details?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

let csrfToken = '';
export function setCsrfToken(t: string) {
  csrfToken = t;
}
function readCsrfCookie(): string {
  const m = document.cookie.match(/(?:^|;\s*)pirochi_csrf=([^;]+)/);
  return m ? decodeURIComponent(m[1]!) : '';
}

const listeners = new Set<() => void>();
/** Called when the server answers 401 (session expired). */
export function onUnauthorized(fn: () => void) {
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
}

export interface ApiEnvelope<T> {
  data: T;
  meta?: { page: number; pageSize: number; total: number };
  [k: string]: unknown;
}

async function request<T>(method: string, url: string, body?: unknown): Promise<T> {
  const isForm = typeof FormData !== 'undefined' && body instanceof FormData;
  const headers: Record<string, string> = { Accept: 'application/json' };
  if (body !== undefined && !isForm) headers['Content-Type'] = 'application/json';
  if (method !== 'GET') headers['x-csrf-token'] = csrfToken || readCsrfCookie();
  let res: Response;
  try {
    res = await fetch('/api/v1' + url, {
      method,
      credentials: 'include',
      headers,
      body: body === undefined ? undefined : isForm ? (body as FormData) : JSON.stringify(body),
    });
  } catch {
    throw new ApiError(0, 'NETWORK', 'تعذر الاتصال بالخادم — تحقق من الشبكة');
  }
  const isJson = (res.headers.get('content-type') || '').includes('application/json');
  const payload = isJson ? await res.json().catch(() => null) : null;
  if (!res.ok) {
    if (res.status === 401 && !url.startsWith('/auth/login')) listeners.forEach((fn) => fn());
    const e = payload?.error;
    throw new ApiError(res.status, e?.code || 'HTTP_' + res.status, e?.message || 'حدث خطأ غير متوقع', e?.details);
  }
  return payload as T;
}

export const api = {
  get: <T>(url: string) => request<ApiEnvelope<T>>('GET', url),
  post: <T>(url: string, body?: unknown) => request<ApiEnvelope<T>>('POST', url, body ?? {}),
  put: <T>(url: string, body?: unknown) => request<ApiEnvelope<T>>('PUT', url, body ?? {}),
  patch: <T>(url: string, body?: unknown) => request<ApiEnvelope<T>>('PATCH', url, body ?? {}),
  del: <T>(url: string) => request<ApiEnvelope<T>>('DELETE', url),
  upload: async (file: File, kind: 'product' | 'quote' = 'product') => {
    const fd = new FormData();
    fd.append('file', file);
    return (await request<ApiEnvelope<{ url: string }>>('POST', '/uploads/image?kind=' + kind, fd)).data.url;
  },
  raw: request,
};

/** Builds a query string, dropping empty values. */
export function qs(params: Record<string, unknown>): string {
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) if (v !== undefined && v !== null && v !== '') p.set(k, String(v));
  const s = p.toString();
  return s ? '?' + s : '';
}
